import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SEO from "../components/SEO"; // ändra sökväg om din SEO ligger någon annanstans

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: radial-gradient(1200px 400px at 20% 0%, rgba(15, 118, 110, 0.10), transparent 55%),
    radial-gradient(900px 360px at 90% 10%, rgba(195, 122, 71, 0.14), transparent 55%),
    #f7fafc;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Hero = styled.header`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.08);
  border-radius: 22px;
  padding: 22px;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 3vw, 44px);
  color: #111827;
  font-weight: 950;
  line-height: 1.12;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #6b7280;
  font-weight: 800;
  line-height: 1.6;
  max-width: 820px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.07);
  border-radius: 22px;
  overflow: hidden;
`;

const Body = styled.div`
  padding: 18px;
  display: grid;
  gap: 14px;
`;

const Block = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const H2 = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const P = styled.p`
  margin: 0;
  color: #374151;
  line-height: 1.8;
  font-weight: 650;
`;

const Ul = styled.ul`
  margin: 10px 0 0 18px;
  color: #374151;
  line-height: 1.8;
  font-weight: 650;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;
  transition: transform 140ms ease, background 140ms ease;

  &:hover {
    background: #f9fafb;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

const Primary = styled(Btn)`
  border: 1px solid rgba(15, 118, 110, 0.25);
  background: rgba(15, 118, 110, 0.12);
  color: #0f766e;
`;

const Danger = styled(Btn)`
  border: 1px solid rgba(233, 78, 27, 0.25);
  background: rgba(233, 78, 27, 0.10);
  color: #c2410c;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-weight: 950;
  color: #111827;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
  background: ${(p) =>
    p.$state === "accepted" ? "rgba(15,118,110,0.9)" :
    p.$state === "declined" ? "rgba(233,78,27,0.9)" :
    "rgba(107,114,128,0.9)"};
`;

const CookiesPage = () => {
  const [consent, setConsent] = useState("unknown");

  useEffect(() => {
    const saved = localStorage.getItem("cookieConsent");
    setConsent(saved || "unknown");
  }, []);

  const setAccepted = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setConsent("accepted");
  };

  const setDeclined = () => {
    localStorage.setItem("cookieConsent", "declined");
    setConsent("declined");
  };

  const reset = () => {
    localStorage.removeItem("cookieConsent");
    setConsent("unknown");
  };

  const statusText =
    consent === "accepted" ? "Accepterat" :
    consent === "declined" ? "Avvisat" :
    "Ej valt ännu";

  return (
    <>
      <SEO
        title="Cookies"
        description="Information om cookies på Hofors BGK."
        keywords="cookies, kakor, integritet, hofors bgk"
      />

      <Page>
        <Container>
          <Hero>
            <Title>Cookies</Title>
            <Subtitle>
              Här förklarar vi vad cookies är, varför vi använder dem och hur du kan välja.
            </Subtitle>
          </Hero>

          <Card>
            <Body>
              <Block>
                <H2>Vad är cookies?</H2>
                <P>
                  Cookies (kakor) är små textfiler som sparas i din webbläsare.
                  De hjälper webbplatsen att komma ihåg val, förbättra funktioner och ge en bättre upplevelse.
                </P>
              </Block>

              <Block>
                <H2>Varför använder vi cookies?</H2>
                <Ul>
                  <li>För att sidan ska fungera smidigt (t.ex. spara ditt val om cookies).</li>
                  <li>För att förbättra användarupplevelsen och navigeringen.</li>
                  <li>Om vi använder statistik/analys i framtiden kan vi be om separat samtycke.</li>
                </Ul>
              </Block>

              <Block>
                <H2>Vilka cookies använder vi just nu?</H2>
                <P>
                  Just nu sparar vi endast ditt val via <b>localStorage</b> (”cookieConsent”).
                  Det är ingen spårning och inga externa annonscookies.
                </P>
              </Block>

              <Block>
                <H2>Ditt val</H2>
                <Actions>
                  <StatusPill>
                    <Dot $state={consent} />
                    Status: {statusText}
                  </StatusPill>

                  <BtnRow>
                    <Primary type="button" onClick={setAccepted}>Acceptera</Primary>
                    <Danger type="button" onClick={setDeclined}>Avvisa</Danger>
                    <Btn type="button" onClick={reset}>Återställ</Btn>
                    <Btn type="button" onClick={() => (window.location.href = "/")}>Tillbaka</Btn>
                  </BtnRow>
                </Actions>
              </Block>

              <Block>
                <H2>Kontakt</H2>
                <P>
                  Frågor? Mejla oss på <b>Hoforsbangolf@gmail.com</b>.
                </P>
              </Block>
            </Body>
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default CookiesPage;
